import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Quote, ArrowRight } from "lucide-react";

interface ExpertOpinionsSectionProps {
  items: any[];
  isLoading: boolean;
}

export function ExpertOpinionsSection({ items, isLoading }: ExpertOpinionsSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="expert-opinions-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Expert Voices"
          icon={Quote}
          title="Expert Opinions"
          href="/leaders-speak"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <blockquote className="text-sm text-muted-foreground leading-relaxed line-clamp-4 italic border-l-2 border-primary/20 pl-4">
                    {typeof item.content === "string" ? item.content.slice(0, 200) : ""}
                  </blockquote>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-heading font-bold text-sm">
                      {item.author?.charAt(0) ?? "?"}
                    </div>
                    <div>
                      <p className="text-sm font-heading font-semibold text-foreground">{item.author}</p>
                      <p className="text-xs text-muted-foreground">{item.authorTitle ?? "Expert"}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-xs gap-1">
                      <Link href={`/news/${item.id}`}>
                        Read more <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={Quote} description="No expert opinions yet." />
        )}
      </div>
    </section>
  );
}